import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  BackArrow,
  Button,
  Input,
  Select,
  TopProgress,
  Typography,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { zonesService } from "../../../../api";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import { formatDate } from "../../../../utils/date-formatter";
import { usePageTitle } from "../../../../context/navigationContext";
import {
  Container,
  Header,
  Content,
  InfoSection,
  InfoRow,
  InfoLabel,
  InfoValue,
  RecordsSection,
  SectionTitle,
  RecordsetsList,
  RecordsetItem,
  RecordsetHeader,
  RecordsetBody,
  RecordField,
  RecordRow,
  RecordActions,
  AddRecordButton,
  ReadOnlyRecordset,
  ActionButtons,
  FloatingAddButton,
} from "./styles";
import { icons } from "../../../../icons";
import StyledIcon from "../../../../components/styled-icon";

type Zone = {
  id: string;
  name: string;
  email: string;
  description: string;
  ttl: number;
  type: string;
  status: string;
  serial: number;
  created_at: string;
  updated_at: string;
  project_id: string;
  version: number;
  masters: any[];
  attributes: any;
  service?: any;
};

type RecordField = {
  [key: string]: string;
};

type Recordset = {
  id?: string;
  name: string;
  type: string;
  ttl: number;
  records: string[];
  status?: string;
  zone_id?: string;
  zone_name?: string;
  project_id?: string;
  changed?: boolean;
  deleted?: boolean;
  created?: boolean;
};

const RECORD_TYPES = [
  "A",
  "AAAA",
  "CNAME",
  "MX",
  "TXT",
  "NS",
  "SRV",
  "SSHFP",
  "PTR",
  "SOA",
];

// Helper functions for different record types
const getFieldNames = (recordType: string): string[] => {
  switch (recordType) {
    case "MX":
      return ["priority", "server"];
    case "SSHFP":
      return ["algorithm", "type", "fingerprint"];
    case "SRV":
      return ["priority", "weight", "port", "hostname"];
    default:
      return ["text"];
  }
};

const textToObj = (text: string, fieldNames: string[]): RecordField => {
  const parts = text.split(" ");
  const obj: RecordField = {};
  for (const [index, fieldName] of fieldNames.entries()) {
    if (index + 1 === fieldNames.length) {
      // last field
      obj[fieldName] = parts.length > index ? parts.slice(index).join(" ") : "";
    } else {
      obj[fieldName] = parts.length > index ? parts[index] : "";
    }
  }
  return obj;
};

const objToText = (obj: RecordField, fieldNames: string[]): string => {
  let text = "";
  for (const fieldName of fieldNames) {
    if (text) {
      text += " ";
    }
    text += obj[fieldName] ? obj[fieldName] : "";
  }
  return text;
};

const ZoneDetails: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { zoneId } = useParams<{ zoneId: string }>();
  const { viewAlert } = useUi();
  usePageTitle(zone?.name ? `${t("DNS_ZONE")}: ${zone.name}` : t("DNS_ZONE"));

  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [zone, setZone] = useState<Zone | null>(null);
  const [recordsets, setRecordsets] = useState<Recordset[]>([]);
  const [readOnlyRecordsets, setReadOnlyRecordsets] = useState<Recordset[]>([]);
  const [editingRecordsetId, setEditingRecordsetId] = useState<string | null>(null);
  const [editedRecordset, setEditedRecordset] = useState<Recordset | null>(null);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [minTtl, setMinTtl] = useState<number>(0);
  const [originalRecordsets, setOriginalRecordsets] = useState<{ [key: string]: Recordset }>({});
  const [recordFields, setRecordFields] = useState<{ [recordIndex: number]: RecordField[] }>({});

  const loadZone = useCallback(() => {
    if (!zoneId) {
      navigate("/openstack/zones");
      return;
    }

    setPageLoading(true);
    zonesService
      .get(zoneId)
      .then((res) => {
        const zoneData = res.data as Zone;
        setZone(zoneData);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_ZONE");
        viewAlert({
          severity: "error",
          message,
        });
        navigate("/openstack/zones");
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [zoneId, t, viewAlert, navigate]);

  const loadRecords = useCallback(() => {
    if (!zoneId) {
      return;
    }

    zonesService
      .listRecords(zoneId)
      .then((res) => {
        const data = res.data;
        setRecordsets(data.recordsets || []);
        setReadOnlyRecordsets(data.read_only_recordsets || []);
        setMinTtl(data.min_ttl || 0);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_RECORDS");
        viewAlert({
          severity: "error",
          message,
        });
      });
  }, [zoneId, t, viewAlert]);

  useEffect(() => {
    loadZone();
    loadRecords();
  }, [loadZone, loadRecords]);

  const handleEditRecordset = useCallback((recordset: Recordset, recordsetIndex: number) => {
    const recordsetKey = recordset.id || `new_${recordsetIndex}`;
    setEditingRecordsetId(recordsetKey);

    // Save original state for cancel functionality
    if (!originalRecordsets[recordsetKey]) {
      setOriginalRecordsets(prev => ({
        ...prev,
        [recordsetKey]: JSON.parse(JSON.stringify(recordset))
      }));
    }

    // Parse records into fields for complex record types
    const fieldNames = getFieldNames(recordset.type);
    const parsedFields = recordset.records.map(record => textToObj(record, fieldNames));
    setRecordFields(prev => ({
      ...prev,
      [recordsetIndex]: parsedFields
    }));

    setEditedRecordset({ ...recordset });
  }, [originalRecordsets]);

  const handleCancelEdit = useCallback((recordsetIndex: number) => {
    const recordset = recordsets[recordsetIndex];
    const recordsetKey = recordset.id || `new_${recordsetIndex}`;

    // Restore original recordset data
    if (originalRecordsets[recordsetKey]) {
      const updatedRecordsets = [...recordsets];
      updatedRecordsets[recordsetIndex] = JSON.parse(JSON.stringify(originalRecordsets[recordsetKey]));
      setRecordsets(updatedRecordsets);

      // Remove from original recordsets cache
      setOriginalRecordsets(prev => {
        const newState = { ...prev };
        delete newState[recordsetKey];
        return newState;
      });
    }

    setEditingRecordsetId(null);
    setEditedRecordset(null);
    setRecordFields(prev => {
      const newState = { ...prev };
      delete newState[recordsetIndex];
      return newState;
    });
  }, [recordsets, originalRecordsets]);

  const handleFieldChange = useCallback(
    (field: string, value: any) => {
      if (editedRecordset) {
        setEditedRecordset({
          ...editedRecordset,
          [field]: value,
          changed: true,
        });
      }
    },
    [editedRecordset]
  );

  const handleRecordChange = useCallback(
    (index: number, value: string) => {
      if (editedRecordset) {
        const newRecords = [...editedRecordset.records];
        newRecords[index] = value;
        setEditedRecordset({
          ...editedRecordset,
          records: newRecords,
          changed: true,
        });
      }
    },
    [editedRecordset]
  );

  const handleAddRecord = useCallback(() => {
    if (editedRecordset) {
      setEditedRecordset({
        ...editedRecordset,
        records: [...editedRecordset.records, ""],
        changed: true,
      });
    }
  }, [editedRecordset]);

  const handleRemoveRecord = useCallback(
    (index: number) => {
      if (editedRecordset) {
        const newRecords = editedRecordset.records.filter((_, i) => i !== index);
        setEditedRecordset({
          ...editedRecordset,
          records: newRecords,
          changed: true,
        });
      }
    },
    [editedRecordset]
  );

  const handleSaveRecordset = useCallback((recordsetIndex: number) => {
    if (!editedRecordset || !zoneId) {
      return;
    }

    // Validate required fields
    if (!editedRecordset.name || !editedRecordset.type) {
      viewAlert({
        severity: "error",
        message: t("PLEASE_FILL_REQUIRED_FIELDS"),
      });
      return;
    }

    // Validate TTL
    if (minTtl && editedRecordset.ttl < minTtl) {
      viewAlert({
        severity: "error",
        message: t("TTL_MUST_BE_AT_LEAST", { min: minTtl }),
      });
      return;
    }

    // Validate records
    if (!editedRecordset.records || editedRecordset.records.length === 0 ||
        editedRecordset.records.some(r => !r || r.trim() === "")) {
      viewAlert({
        severity: "error",
        message: t("PLEASE_FILL_ALL_RECORD_VALUES"),
      });
      return;
    }

    setSaveLoading(true);

    // Update recordsets array with edited recordset
    const updatedRecordsets = [...recordsets];
    updatedRecordsets[recordsetIndex] = { ...editedRecordset, changed: true };
    setRecordsets(updatedRecordsets);

    // Send only the changed recordset to API
    const recordsetsToSend = [editedRecordset];

    zonesService
      .synchronizeRecords(zoneId, recordsetsToSend)
      .then((res) => {
        viewAlert({
          severity: "information",
          message: res.data?.detail || t("RECORDSET_SAVED_SUCCESSFULLY"),
        });

        const recordsetKey = editedRecordset.id || `new_${recordsetIndex}`;
        // Remove from original recordsets cache
        setOriginalRecordsets(prev => {
          const newState = { ...prev };
          delete newState[recordsetKey];
          return newState;
        });

        setEditingRecordsetId(null);
        setEditedRecordset(null);
        setRecordFields(prev => {
          const newState = { ...prev };
          delete newState[recordsetIndex];
          return newState;
        });

        // Reload to get updated data from server
        loadRecords();
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_SAVE_RECORDSET");
        viewAlert({
          severity: "error",
          message,
        });
      })
      .finally(() => {
        setSaveLoading(false);
      });
  }, [editedRecordset, zoneId, recordsets, minTtl, t, viewAlert, loadRecords]);

  const handleDeleteRecordset = useCallback(
    (recordset: Recordset, recordsetIndex: number) => {
      const confirmDelete = window.confirm(
        t("CONFIRM_DELETE_RECORDSET", { name: recordset.name })
      );
      if (!confirmDelete) {
        return;
      }

      if (!zoneId) {
        return;
      }

      setSaveLoading(true);

      // If newly created (not saved yet), just remove from list
      if (recordset.created && !recordset.id) {
        const updatedRecordsets = recordsets.filter((_, idx) => idx !== recordsetIndex);
        setRecordsets(updatedRecordsets);
        setSaveLoading(false);
        viewAlert({
          severity: "information",
          message: t("RECORDSET_DELETED_SUCCESSFULLY"),
        });
        return;
      }

      // Mark as deleted and send to API
      const recordsetToDelete = { ...recordset, deleted: true };

      zonesService
        .synchronizeRecords(zoneId, [recordsetToDelete])
        .then((res) => {
          viewAlert({
            severity: "information",
            message: res.data?.detail || t("RECORDSET_DELETED_SUCCESSFULLY"),
          });
          loadRecords();
        })
        .catch((error) => {
          const message =
            error?.response?.data?.detail ||
            extractErrorMessage(error?.response?.data) ||
            t("FAILED_TO_DELETE_RECORDSET");
          viewAlert({
            severity: "error",
            message,
          });
        })
        .finally(() => {
          setSaveLoading(false);
        });
    },
    [zoneId, recordsets, t, viewAlert, loadRecords]
  );

  const handleAddRecordset = useCallback(() => {
    const newRecordset: Recordset = {
      name: zone?.name || "",
      type: "A",
      ttl: zone?.ttl || 1800,
      records: [""],
      created: true,
      changed: true,
      zone_id: zoneId,
    };
    const newRecordsets = [...recordsets, newRecordset];
    setRecordsets(newRecordsets);
    const newIndex = newRecordsets.length - 1;
    handleEditRecordset(newRecordset, newIndex);
  }, [zone, zoneId, recordsets, handleEditRecordset]);

  const handleSaveAllChanges = useCallback(() => {
    if (!zoneId) {
      return;
    }

    // Filter to only send changed or deleted recordsets
    const recordsetsToSend = recordsets.filter((rs) => rs.changed || rs.deleted);

    if (recordsetsToSend.length === 0) {
      viewAlert({
        severity: "information",
        message: t("NO_CHANGES_TO_SAVE"),
      });
      return;
    }

    // Validate all changed recordsets
    for (const rs of recordsetsToSend) {
      if (!rs.deleted) {
        if (!rs.name || !rs.type) {
          viewAlert({
            severity: "error",
            message: t("PLEASE_FILL_REQUIRED_FIELDS"),
          });
          return;
        }
        if (minTtl && rs.ttl < minTtl) {
          viewAlert({
            severity: "error",
            message: t("TTL_MUST_BE_AT_LEAST", { min: minTtl }),
          });
          return;
        }
        if (!rs.records || rs.records.length === 0 ||
            rs.records.some(r => !r || r.trim() === "")) {
          viewAlert({
            severity: "error",
            message: t("PLEASE_FILL_ALL_RECORD_VALUES"),
          });
          return;
        }
      }
    }

    setSaveLoading(true);
    zonesService
      .synchronizeRecords(zoneId, recordsetsToSend)
      .then((res) => {
        viewAlert({
          severity: "information",
          message: res.data?.detail || t("ALL_CHANGES_SAVED_SUCCESSFULLY"),
        });
        setEditingRecordsetId(null);
        setEditedRecordset(null);
        setOriginalRecordsets({});
        setRecordFields({});
        loadRecords();
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_SAVE_CHANGES");
        viewAlert({
          severity: "error",
          message,
        });
      })
      .finally(() => {
        setSaveLoading(false);
      });
  }, [zoneId, recordsets, minTtl, t, viewAlert, loadRecords]);

  const recordTypeOptions = useMemo(
    () =>
      RECORD_TYPES.map((type) => ({
        id: type,
        value: type,
        label: type,
      })),
    []
  );

  const hasChanges = useMemo(
    () => recordsets.some((rs) => rs.changed),
    [recordsets]
  );

  if (pageLoading || !zone) {
    return (
      <Container>
        <TopProgress loading={true} />
        <Typography variant="body">{t("LOADING")}</Typography>
      </Container>
    );
  }

  return (
    <Container>
      <TopProgress loading={saveLoading} />
      <Header>
        <BackArrow fallbackPath="/openstack/zones" />
        <Typography variant="h1">{zone.name}</Typography>
        <ActionButtons>
          <Button
            variant="stroke"
            size="display"
            title={t("EDIT")}
            onClick={() => navigate(`/openstack/zones/${zoneId}/edit`)}
          />
          {hasChanges && (
            <Button
              variant="primary"
              size="display"
              title={t("SAVE_ALL_CHANGES")}
              loading={saveLoading}
              onClick={handleSaveAllChanges}
            />
          )}
        </ActionButtons>
      </Header>

      <Content>
        <InfoSection>
          <SectionTitle>
            <Typography variant="h2">{t("ZONE_INFORMATION")}</Typography>
          </SectionTitle>
          <InfoRow>
            <InfoLabel>{t("ID")}:</InfoLabel>
            <InfoValue>{zone.id}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("STATUS")}:</InfoLabel>
            <InfoValue>{zone.status}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("PROJECT_ID")}:</InfoLabel>
            <InfoValue>{zone.project_id}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("EMAIL")}:</InfoLabel>
            <InfoValue>{zone.email}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("TTL")}:</InfoLabel>
            <InfoValue>{zone.ttl}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("ZONE_TYPE")}:</InfoLabel>
            <InfoValue>{zone.type}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("SERIAL")}:</InfoLabel>
            <InfoValue>{zone.serial}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("VERSION")}:</InfoLabel>
            <InfoValue>{zone.version}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("CREATED_AT")}:</InfoLabel>
            <InfoValue>{formatDate(zone.created_at)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t("UPDATED_AT")}:</InfoLabel>
            <InfoValue>{formatDate(zone.updated_at)}</InfoValue>
          </InfoRow>
          {zone.description && (
            <InfoRow>
              <InfoLabel>{t("DESCRIPTION")}:</InfoLabel>
              <InfoValue>{zone.description}</InfoValue>
            </InfoRow>
          )}
        </InfoSection>

        <RecordsSection>
          <SectionTitle>
            <Typography variant="h2">{t("DNS_RECORDS")}</Typography>
          </SectionTitle>

          {readOnlyRecordsets.length > 0 && (
            <>
              <Typography variant="h3" style={{ marginTop: "16px", marginBottom: "8px" }}>
                {t("SYSTEM_RECORDS")}
              </Typography>
              {readOnlyRecordsets.map((recordset, index) => (
                <ReadOnlyRecordset key={index}>
                  <div>
                    <strong>{recordset.name}</strong> - {recordset.type} (TTL: {recordset.ttl})
                  </div>
                  {recordset.records.map((record, idx) => (
                    <div key={idx} style={{ marginLeft: "16px", color: "#666" }}>
                      {record}
                    </div>
                  ))}
                </ReadOnlyRecordset>
              ))}
            </>
          )}

          <Typography variant="h3" style={{ marginTop: "24px", marginBottom: "8px" }}>
            {t("EDITABLE_RECORDS")}
          </Typography>

          <RecordsetsList>
            {recordsets.map((recordset, index) => {
              const recordsetKey = recordset.id || `new_${index}`;
              const isEditing = editingRecordsetId === recordsetKey;
              const currentRecordset = isEditing ? editedRecordset : recordset;

              if (!currentRecordset || currentRecordset.deleted) {
                return null;
              }

              return (
                <RecordsetItem key={recordsetKey} isEditing={isEditing}>
                  <RecordsetHeader
                    onClick={() => !isEditing && handleEditRecordset(recordset, index)}
                    style={{ cursor: isEditing ? "default" : "pointer" }}
                  >
                    {isEditing ? (
                      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", width: "100%" }}>
                        <Input
                          title={t("SUBDOMAIN")}
                          placeholder="subdomain"
                          value={currentRecordset.name}
                          onChange={(e: any) => handleFieldChange("name", e.target.value)}
                          disabled={!currentRecordset.created}
                          fieldName="name"
                          values={{ name: currentRecordset.name }}
                          errors={{}}
                          touched={{}}
                          handleChange={() => {}}
                          handleBlur={() => {}}
                        />
                        <Select
                          title={t("TYPE")}
                          data={recordTypeOptions}
                          selectedValue={currentRecordset.type}
                          onChange={(value: string) => handleFieldChange("type", value)}
                          placeholder={t("CHOOSE")}
                          disabled={!currentRecordset.created}
                          fieldName="type"
                          values={{ type: currentRecordset.type }}
                          errors={{}}
                          touched={{}}
                          handleChange={() => {}}
                          handleBlur={() => {}}
                        />
                        <Input
                          title={t("TTL")}
                          placeholder="1800"
                          type="number"
                          value={currentRecordset.ttl}
                          onChange={(e: any) => handleFieldChange("ttl", parseInt(e.target.value))}
                          fieldName="ttl"
                          values={{ ttl: currentRecordset.ttl }}
                          errors={{}}
                          touched={{}}
                          handleChange={() => {}}
                          handleBlur={() => {}}
                        />
                      </div>
                    ) : (
                      <div>
                        <strong>{recordset.name}</strong> - {recordset.type} (TTL: {recordset.ttl})
                        {recordset.status === "ERROR" && (
                          <span style={{ color: "red", marginLeft: "8px" }}>ERROR</span>
                        )}
                      </div>
                    )}
                  </RecordsetHeader>

                  {isEditing && (
                    <RecordsetBody>
                      <Typography variant="body" style={{ marginBottom: "8px" }}>
                        {t("RECORDS")}:
                      </Typography>
                      {currentRecordset.records.map((record, idx) => (
                        <RecordRow key={idx}>
                          <Input
                            placeholder={t("RECORD_VALUE")}
                            value={record}
                            onChange={(e: any) => handleRecordChange(idx, e.target.value)}
                            fieldName={`record_${idx}`}
                            values={{ [`record_${idx}`]: record }}
                            errors={{}}
                            touched={{}}
                            handleChange={() => {}}
                            handleBlur={() => {}}
                          />
                          {currentRecordset.records.length > 1 && (
                            <Button
                              variant="stroke"
                              size="small"
                              title={t("REMOVE")}
                              onClick={() => handleRemoveRecord(idx)}
                            />
                          )}
                        </RecordRow>
                      ))}
                      <AddRecordButton onClick={handleAddRecord}>
                        <StyledIcon src={icons.Plus} />
                        {t("ADD_RECORD")}
                      </AddRecordButton>

                      <RecordActions>
                        {!recordset.created && (
                          <Button
                            variant="stroke"
                            size="display"
                            title={t("CANCEL")}
                            onClick={() => handleCancelEdit(index)}
                          />
                        )}
                        <Button
                          variant="stroke"
                          size="display"
                          title={t("DELETE_RECORDSET")}
                          isDelete
                          onClick={() => handleDeleteRecordset(recordset, index)}
                        />
                        <Button
                          variant="primary"
                          size="display"
                          title={t("SAVE")}
                          loading={saveLoading}
                          onClick={() => handleSaveRecordset(index)}
                        />
                      </RecordActions>
                    </RecordsetBody>
                  )}
                </RecordsetItem>
              );
            })}
          </RecordsetsList>

          <FloatingAddButton onClick={handleAddRecordset}>
            <StyledIcon src={icons.Plus} />
            {t("ADD_RECORDSET")}
          </FloatingAddButton>
        </RecordsSection>
      </Content>
    </Container>
  );
};

export default ZoneDetails;
